import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { api } from "../../api";
import { RefChip } from "../../components/RefChip";
import { SortableTable } from "../../components/SortableTable";
import type { Column } from "../../components/SortableTable";
import { CardCollection, ViewToggle, useViewMode } from "../../components/ViewToggle";
import { AuthorChip, Disclaimer } from "../../components/social";
import { date, dd, num, pct, tone } from "../../format";
import { MarketTag } from "../../components/MarketTag";
import { MarketTypeFilter } from "../../components/MarketTypeFilter";
import { matchesMarketType } from "../../components/marketType";
import type { MarketTypeChoice } from "../../components/marketType";
import { useSession } from "../../session";
import { Card, EmptyState, PageHead, Stat } from "../../shell/ui";
import { directionColumn } from "../../components/direction";

type Phase = "upcoming" | "running" | "finished";

interface Season {
  id: string;
  title: string;
  description: string;
  starts_at: string;
  ends_at: string;
  kind: "monthly" | "custom";
  phase: Phase;
  final: boolean;
  participants?: number;
  ranked?: number;
  leader?: { bot_id: string; bot_name: string; return_pct: number } | null;
}

interface StandingRow {
  bot_id: string;
  owner_id: string;
  bot_name: string;
  bot_exists: boolean;
  symbol: string;
  timeframe: string;
  market_type?: string;
  leverage?: number;
  direction?: "long" | "short";
  joined_ts: number;
  return_pct: number;
  max_drawdown_pct: number;
  trades: number;
  win_rate_pct: number | null;
  score: number;
  /** null: in the season, but no closed trade in it yet. */
  rank: number | null;
  owner: {
    username: string;
    name: string;
    avatar: string;
    simulated: boolean;
    badge: "verified" | null;
  } | null;
  ref: { namespace: string; slug: string; version: number | null; digest: string; text: string } | null;
}

const PHASE_LABEL: Record<Phase, string> = {
  upcoming: "Starts soon",
  running: "Running",
  finished: "Finished",
};

function PhaseBadge({ s }: { s: Pick<Season, "phase" | "final"> }) {
  return (
    <span className={`badge league-phase league-phase--${s.phase}`}>
      {s.phase === "finished" && !s.final ? "Ended · final table within the hour" : PHASE_LABEL[s.phase]}
    </span>
  );
}

/** "3d 4h" until a moment; re-renders every minute. */
function Countdown({ to }: { to: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  const ms = new Date(to).getTime() - now;
  if (ms <= 0) return <>now</>;
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return <>{d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`}</>;
}

function Explainer() {
  return (
    <p className="muted">
      <strong>Every public paper bot takes part, automatically.</strong> A season is a stretch of time, one
      per month; each bot is ranked on what it really did in it, from its live trades, which nobody can have
      tuned in advance. Nothing to sign up for: make a bot public and it is in.
    </p>
  );
}

export function LeaguePage() {
  const navigate = useNavigate();
  const [view, setView] = useViewMode("league", "grid");
  const list = useQuery({
    queryKey: ["league"],
    queryFn: () => api<{ items: Season[]; metricLabel: string }>("/hub/league/seasons"),
    refetchInterval: 60_000,
  });
  const items = list.data?.items ?? [];
  return (
    <div className="stack">
      <PageHead title="Strategy League">
        <ViewToggle value={view} onChange={setView} />
      </PageHead>
      <Explainer />
      {view === "details" ? (
        <Card flush>
          <SortableTable
            exportName="league-seasons"
            rows={items}
            loading={list.isPending}
            rowKey={(s) => s.id}
            onRowClick={(s) => navigate(`/league/${s.id}`)}
            initialSort={{ key: "starts", direction: "desc" }}
            columns={SEASON_COLUMNS}
            caption="League seasons"
          />
        </Card>
      ) : (
        <CardCollection mode={view}>
          {items.map((s) => (
            <Link key={s.id} to={`/league/${s.id}`} className="card league-card">
              <div className="card__body stack">
                <div className="row league-card__head">
                  <h2>{s.title}</h2>
                  <PhaseBadge s={s} />
                </div>
                <p className="small muted">
                  {date(s.starts_at)} – {date(s.ends_at)}
                </p>
                {s.description && <p className="small">{s.description}</p>}
                <div className="league-card__facts small collection-numbers">
                  <span>
                    {s.phase === "running" ? (
                      <>
                        Ends in{" "}
                        <strong>
                          <Countdown to={s.ends_at} />
                        </strong>
                      </>
                    ) : s.phase === "upcoming" ? (
                      <>
                        Starts in{" "}
                        <strong>
                          <Countdown to={s.starts_at} />
                        </strong>
                      </>
                    ) : (
                      "Final"
                    )}
                  </span>
                  <span>
                    {s.participants ?? 0} bots, {s.ranked ?? 0} ranked
                  </span>
                </div>
                {s.leader && (
                  <p className="small league-card__leader">
                    {s.phase === "finished" ? "Winner" : "Leading"}: <strong>{s.leader.bot_name}</strong>{" "}
                    <span className={tone(s.leader.return_pct)}>{pct(s.leader.return_pct, 2)}</span>
                  </p>
                )}
              </div>
            </Link>
          ))}
        </CardCollection>
      )}
      <Disclaimer />
    </div>
  );
}

const SEASON_COLUMNS: Column<Season>[] = [
  {
    key: "title",
    label: "Season",
    // A real link, so the row opens from the keyboard too (audit F-015); the
    // row click stays for the mouse.
    render: (s) => (
      <Link to={`/league/${s.id}`}>
        <strong>{s.title}</strong>
      </Link>
    ),
    sortValue: (s) => s.title,
    firstDirection: "asc",
  },
  { key: "phase", label: "Phase", render: (s) => <PhaseBadge s={s} />, sortValue: (s) => s.phase },
  {
    key: "starts",
    label: "From",
    align: "right",
    render: (s) => date(s.starts_at),
    sortValue: (s) => new Date(s.starts_at).getTime(),
  },
  {
    key: "ends",
    label: "Until",
    align: "right",
    render: (s) => date(s.ends_at),
    sortValue: (s) => new Date(s.ends_at).getTime(),
  },
  {
    key: "bots",
    label: "Bots",
    align: "right",
    render: (s) => s.participants ?? 0,
    sortValue: (s) => s.participants ?? 0,
  },
  {
    key: "leader",
    label: "Leader",
    render: (s) => (s.leader ? `${s.leader.bot_name} (${pct(s.leader.return_pct, 2)})` : "—"),
    sortValue: (s) => s.leader?.return_pct ?? null,
  },
];

export function SeasonPage() {
  const { id = "" } = useParams();
  const { user } = useSession();
  const navigate = useNavigate();
  const [marketType, setMarketType] = useState<MarketTypeChoice>("all");
  const q = useQuery({
    queryKey: ["league", id],
    queryFn: () =>
      api<{ season: Season; metricLabel: string; minRankedTrades: number; standings: StandingRow[] }>(
        `/hub/league/seasons/${id}`,
      ),
    refetchInterval: 60_000,
  });
  if (q.isLoading) return <p className="muted">Loading…</p>;
  if (q.error || !q.data)
    return (
      <Card>
        <EmptyState icon="🏁" title="No such season">
          {q.error?.message}
        </EmptyState>
      </Card>
    );
  const { season: s, standings, metricLabel } = q.data;
  const mine = user
    ? standings.filter((r) => r.owner?.username.toLowerCase() === user.username.toLowerCase())
    : [];
  // A bot is ranked once it closed a trade in the season; the others take part, shown apart.
  // The market filter narrows what is shown, never the ranking: "#" stays the rank among all.
  const shown = standings.filter((r) => matchesMarketType(marketType, r.market_type));
  const ranked = shown.filter((r) => r.rank !== null);
  const unranked = shown.filter((r) => r.rank === null);
  const leader = standings.find((r) => r.rank !== null);
  const start = new Date(s.starts_at).getTime();

  const columns: Column<StandingRow>[] = [
    {
      key: "rank",
      label: "#",
      align: "right",
      render: (r) => r.rank,
      sortValue: (r) => r.rank,
      firstDirection: "asc",
    },
    {
      key: "bot",
      label: "Bot",
      render: (r) => (
        <span className="league-entry">
          {r.bot_exists ? <Link to={`/b/${r.bot_id}`}>{r.bot_name}</Link> : <span>{r.bot_name}</span>}
          {r.ref && <RefChip namespace={r.ref.namespace} slug={r.ref.slug} version={r.ref.version} compact />}
        </span>
      ),
      sortValue: (r) => r.bot_name,
      firstDirection: "asc",
    },
    {
      key: "owner",
      label: "Owner",
      render: (r) =>
        r.owner ? (
          <AuthorChip
            username={r.owner.username}
            name={r.owner.name}
            avatar={r.owner.avatar}
            simulated={r.owner.simulated}
            badge={r.owner.badge}
          />
        ) : (
          "—"
        ),
      sortValue: (r) => r.owner?.name ?? null,
      firstDirection: "asc",
    },
    {
      key: "market",
      label: "Market",
      render: (r) => (
        <span className="mono small">
          {r.symbol} {r.timeframe} <MarketTag marketType={r.market_type} leverage={r.leverage} />
        </span>
      ),
      sortValue: (r) => `${r.symbol} ${r.timeframe}`,
      firstDirection: "asc",
    },
    directionColumn(),
    {
      key: "ret",
      label: "Return",
      align: "right",
      render: (r) => <span className={tone(r.return_pct)}>{pct(r.return_pct, 2)}</span>,
      sortValue: (r) => r.return_pct,
    },
    {
      key: "dd",
      label: "Max DD",
      align: "right",
      render: (r) => dd(r.max_drawdown_pct),
      sortValue: (r) => r.max_drawdown_pct,
      firstDirection: "asc",
    },
    { key: "trades", label: "Trades", align: "right", render: (r) => r.trades, sortValue: (r) => r.trades },
    {
      key: "win",
      label: "Win rate",
      align: "right",
      render: (r) => pct(r.win_rate_pct, 0, false),
      sortValue: (r) => r.win_rate_pct,
    },
    {
      key: "score",
      label: "Score",
      align: "right",
      title: metricLabel,
      render: (r) => <strong>{num(r.score, 2)}</strong>,
      sortValue: (r) => r.score,
    },
    {
      key: "joined",
      label: "In since",
      align: "right",
      render: (r) => (r.joined_ts > start + 3_600_000 ? date(r.joined_ts) : "the start"),
      sortValue: (r) => r.joined_ts,
      firstDirection: "asc",
    },
  ];

  return (
    <div className="stack">
      <header className="detail__head">
        <div>
          <p className="muted small">
            <Link to="/league">Strategy League</Link> ·{" "}
            {s.kind === "monthly" ? "monthly season" : "special season"}
          </p>
          <h1 className="page__title">{s.title}</h1>
          <p className="detail__meta">
            <PhaseBadge s={s} />
            <span className="muted small">
              {date(s.starts_at, true)} – {date(s.ends_at, true)}
            </span>
          </p>
        </div>
      </header>
      {s.description && <p>{s.description}</p>}
      <Explainer />
      <div className="stat-grid">
        <Stat
          label="Bots taking part"
          value={String(standings.length)}
          hint={`${ranked.length} ranked; a bot is ranked after its first closed trade in the season`}
        />
        <Stat
          label={s.phase === "running" ? "Ends in" : s.phase === "upcoming" ? "Starts in" : "Ended"}
          value={
            s.phase === "finished" ? (
              date(s.ends_at)
            ) : (
              <Countdown to={s.phase === "upcoming" ? s.starts_at : s.ends_at} />
            )
          }
        />
        <Stat label={s.phase === "finished" ? "Winner" : "Leading"} value={leader ? leader.bot_name : "—"} />
        <Stat
          label="Ranked by"
          value={metricLabel}
          hint="Return divided by the worst drawdown in the season (at least 1%)"
        />
      </div>
      {mine.length > 0 && (
        <p className="small">
          {mine.length === 1 ? "Your bot: " : `Your ${mine.length} bots, best first: `}
          {mine.slice(0, 3).map((r, i) => (
            <span key={r.bot_id}>
              {i > 0 && " · "}
              <strong>
                {r.rank === null ? "unranked" : `#${r.rank}`} {r.bot_name}
              </strong>
            </span>
          ))}
          {mine.length > 3 && ` and ${mine.length - 3} more`} ({ranked.length} ranked)
        </p>
      )}
      <Card
        title={s.final ? "Final standings" : s.phase === "running" ? "Standings, live" : "Standings"}
        flush
      >
        {s.phase === "running" && (
          <p className="card__body small muted">
            Calculated from the bots' live trades and refreshed every minute. The table is frozen when the
            season ends.
          </p>
        )}
        {standings.length > 0 && (
          <div className="card__body row league-filter">
            <MarketTypeFilter value={marketType} onChange={setMarketType} />
            {marketType !== "all" && (
              <span className="small muted">
                {shown.length} of {standings.length} bots. # is still each bot&apos;s place among all of them.
              </span>
            )}
          </div>
        )}
        <SortableTable
          exportName={["league", s.title]}
          rows={ranked}
          rowKey={(r) => r.bot_id}
          onRowClick={(r) => r.bot_exists && navigate(`/b/${r.bot_id}`)}
          initialSort={{ key: "rank", direction: "asc" }}
          columns={columns}
          caption={`${s.title} standings`}
          empty={
            <EmptyState
              icon="🏁"
              title={
                s.phase === "upcoming"
                  ? "Not started yet"
                  : standings.length
                    ? "No bot has closed a trade yet"
                    : "No public bots ran in this season"
              }
            >
              {s.phase === "upcoming"
                ? `It starts on ${date(s.starts_at, true)}.`
                : standings.length
                  ? "A bot is ranked after its first closed trade in the season."
                  : "Make a bot public and it takes part."}
            </EmptyState>
          }
        />
        {unranked.length > 0 && (
          <details className="league-unranked">
            <summary className="small">
              {unranked.length} {unranked.length === 1 ? "bot has" : "bots have"} not closed a trade in this
              season{s.final ? ", so not ranked" : " yet: not ranked until they do"}
            </summary>
            <SortableTable
              exportName={["league-unranked", s.title]}
              rows={unranked}
              rowKey={(r) => r.bot_id}
              onRowClick={(r) => r.bot_exists && navigate(`/b/${r.bot_id}`)}
              initialSort={{ key: "ret", direction: "desc" }}
              columns={columns.filter((c) => !["rank", "trades", "win", "score"].includes(c.key as string))}
              caption={`${s.title}: bots without a closed trade`}
            />
          </details>
        )}
      </Card>
      <Disclaimer />
    </div>
  );
}
